import MyContainer from '@/components/MyContainer';
import { BulkReceiveModal } from '@/components/products/BulkReceiveModal';
import { ProductFilters } from '@/components/products/product-filters';
import StockClosingCard from '@/components/stock/StockClosingCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useBulkReceiveStock } from '@/hooks/product/use-bulk-receive-stock';
import { useStockPage } from '@/pages/stock/hooks/useStockPage';
import { routes } from '@/routes/route-constants';
import { Stack } from '@mui/material';
import { AlertTriangle, PackagePlus, PlusCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { useProductContext } from '../context/product-context';
import { ProductTreeTable } from './product-tree-table';

export function ProductPageContent() {
  const { t } = useTranslation(['product', 'stock', 'common']);
  const [showBulkReceiveModal, setShowBulkReceiveModal] = useState(false);
  const [, setLocation] = useLocation();
  const { handleRecomputeAllStock } = useStockPage();
  const { variants, isLoading, error, refresh, pagination, page, setPage, pageSize, setPageSize } =
    useProductContext();

  const { handleBulkReceiveStock } = useBulkReceiveStock();

  const handleAddProduct = () => {
    setLocation(routes.product.new());
  };

  if (error) {
    return (
      <MyContainer
        title="Products"
        enableBack={false}
        subtitle="Manage your product catalog across all stores."
      >
        <div className="space-y-6 p-4">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="text-center">
                <AlertTriangle className="h-12 w-12 text-red-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-red-600">Error loading products</h3>
                <p className="text-muted-foreground">
                  {error.message || 'Failed to load products. Please try again.'}
                </p>
                <Button className="mt-4" onClick={() => window.location.reload()}>
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </MyContainer>
    );
  }

  return (
    <MyContainer
      title={t('product:title')}
      enableBack={false}
      subtitle={t('product:createDescription', { defaultValue: 'Manage your product catalog across all stores.' })}
      actions={
        <>
          <Button
            size="sm"
            onClick={() => {
              setShowBulkReceiveModal(true);
            }}
            variant="outline"
          >
            <PackagePlus className="mr-2 h-4 w-4" />
            <span>{t('stock:receiveStock')}</span>
          </Button>
          <Button onClick={handleAddProduct} size="sm">
            <PlusCircle className="mr-2 h-4 w-4" />
            {t('product:createProduct')}
          </Button>
        </>
      }
    >
      <div className="w-full max-w-full overflow-x-hidden">
        <Stack spacing={2} sx={{ width: '100%', maxWidth: '100%' }}>
          <StockClosingCard onRunClosing={handleRecomputeAllStock} />

          {/* Filters */}
          <ProductFilters />

          {/* Tree Table */}
          <ProductTreeTable
            variants={variants}
            isLoading={isLoading}
            pagination={pagination}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            onRefresh={refresh}
          />

          {/* Bulk Receive Modal */}
          <BulkReceiveModal
            isOpen={showBulkReceiveModal}
            productId={null}
            onSubmit={handleBulkReceiveStock}
            onCancel={() => setShowBulkReceiveModal(false)}
          />
        </Stack>
      </div>
    </MyContainer>
  );
}
